import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import moment from "moment"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import { useTheme } from "context/AntdThemeContext"
import {
  getDefaultCalendarColor,
  getPaymentAccentColor,
  getPrimaryCalendarTextColor,
  resolveBookingColors
} from "pagesV2/pro/Calendar/calendarColorUtils"
import styles from "./index.module.css"
import BookingPopover from "pagesV2/pro/Calendar/shared/BookingPopover"
import BookingConflictWarning from "pagesV2/pro/Calendar/Tooltips/BookingConflictWarning"

/** "9 AM", not "9:00 AM": a month cell is ~210px wide and whole hours are the common case. */
const formatTileTime = (value) => {
  if (!value) return ""
  const parsed = moment(value, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])
  if (!parsed.isValid()) return ""
  return parsed.format(parsed.minute() === 0 ? "h A" : "h:mm A")
}

/**
 * One booking in a month day cell: an accent bar, then the start time and the client's name
 * sharing a baseline, with the guest count trailing when the experience's capacity is known.
 *
 * The bar carries the payment state and the background carries the experience colour, so the
 * tile answers "which experience" and "is it paid" at once — see the calendar Legend. Details
 * beyond that (guests, waiver, location) live in the hover popover; the tile is one line.
 */
const BookingItem = ({ booking, conflict = false, isOnBlackout = false, setIsBookingModalOpen, setSelectedBookingId }) => {
  const { start_time, uuid, client_name, color, total_guest_count, max_guests, payment_status } = booking || {}
  const { theme } = useTheme()

  const primaryTextColor = getPrimaryCalendarTextColor(theme)
  const previewColors = resolveBookingColors(color, getDefaultCalendarColor(), primaryTextColor, getEventColor)
  const accentColor = getPaymentAccentColor(payment_status) || previewColors.borderColor

  // Capacity is not on the calendar booking payload yet, so the counter renders only where the
  // experience's `max_guests` happens to come through. See docs/blackouts-api.md.
  const guestCount = Number(total_guest_count) || 0
  const capacity = Number(max_guests) || 0
  const guestLabel = guestCount && capacity ? `${guestCount}/${capacity}` : ""

  // Booking tints are low-alpha and assume a white cell behind them. A blacked-out cell body is
  // dark or grey, so the tint is painted as a layer over an opaque white base — that keeps the
  // tile light whatever form the colour takes (hex, rgba or a CSS variable).
  const tint = previewColors.backgroundColor

  const handleBookingClick = () => {
    setIsBookingModalOpen(true)
    setSelectedBookingId(uuid)
  }

  return (
    <BookingPopover booking={booking} colors={previewColors}>
      <Button
        type="link"
        className={`${styles.tile} ${isOnBlackout ? "" : styles.tileOnWhite}`}
        style={{ backgroundImage: `linear-gradient(${tint}, ${tint})` }}
        onClick={handleBookingClick}
      >
        <span className={styles.accent} style={{ backgroundColor: accentColor }} aria-hidden />
        <span className={styles.content}>
          {start_time && <span className={styles.time}>{formatTileTime(start_time)}</span>}
          <span className={styles.title}>{client_name}</span>
          {guestLabel && <span className={styles.guests}>{guestLabel}</span>}
          {conflict && <BookingConflictWarning />}
        </span>
      </Button>
    </BookingPopover>
  )
}

BookingItem.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    payment_status: PropTypes.string,
    title: PropTypes.string.isRequired,
    client_name: PropTypes.string,
    start_time: PropTypes.string,
    total_guest_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max_guests: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  /** The booking overlaps scheduled time off; shows the warning tooltip. */
  conflict: PropTypes.bool,
  /** Drops the white outline, which only separates tiles on a white cell. */
  isOnBlackout: PropTypes.bool,
  setIsBookingModalOpen: PropTypes.func,
  setSelectedBookingId: PropTypes.func
}

export default memo(BookingItem)
